import { useEffect, useState } from "react";
import qrcode from "qrcode-generator";
import type { ActivityPresenceSheet, OrganizerWorkspaceCatalog } from "@verity-connect/contracts";
import { formatPresenceCode } from "@verity-connect/domain";

/**
 * The stack of paper, ready for a printer.
 *
 * One page per seminar. The title is set large enough to read from the back row,
 * because the presenter holds this up and forty people have to know they are
 * looking at the right session before they point a camera at it.
 *
 * The characters under the square are for the phone whose camera will not focus
 * and for the teacher who types it in at the hotel that night. They are printed
 * in two groups of four, which is how a person reads a code aloud.
 *
 * Correction to an assumption worth writing down: this page shows what the
 * server generated and never makes a code of its own. Print it twice and both
 * stacks carry the same squares.
 */
export function PresenceSheet({ search }: { search: string }) {
  const [sheet, setSheet] = useState<ActivityPresenceSheet | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        let slug = new URLSearchParams(search).get("event");
        if (!slug) {
          const workspace = await fetch("/api/organizer/workspace", {
            credentials: "same-origin", signal: controller.signal,
          });
          if (workspace.status === 401) throw new Error("Sign in to the staff workspace first, then open this page again.");
          if (!workspace.ok) throw new Error("The staff workspace could not be read.");
          const catalog = await workspace.json() as OrganizerWorkspaceCatalog;
          slug = catalog.events[0]?.slug ?? null;
        }
        if (!slug) throw new Error("No event was found to print pages for.");
        const response = await fetch(
          `/api/organizer/events/${encodeURIComponent(slug)}/presence-sheet`,
          { credentials: "same-origin", signal: controller.signal },
        );
        if (response.status === 401) throw new Error("Sign in to the staff workspace first, then open this page again.");
        if (response.status === 403) throw new Error("This staff account cannot read the convention schedule.");
        if (!response.ok) throw new Error("The pages could not be prepared.");
        if (!controller.signal.aborted) setSheet(await response.json() as ActivityPresenceSheet);
      } catch (reason) {
        if (!controller.signal.aborted) {
          setError(reason instanceof Error ? reason.message : "The pages could not be prepared.");
        }
      }
    })();
    return () => controller.abort();
  }, [search]);

  if (error) return <div className="presence-sheet presence-sheet-message"><h1>Nothing to print yet</h1><p>{error}</p></div>;
  if (!sheet) return <div className="presence-sheet presence-sheet-message"><p>Preparing the pages…</p></div>;

  return (
    <div className="presence-sheet">
      <style>{PRINT_STYLES}</style>
      <div className="presence-sheet-toolbar">
        <p>
          {sheet.pages.length} pages, one for each seminar. Print them, then hand each presenter
          the page for their own session with their room assignment.
        </p>
        <button type="button" onClick={() => window.print()}>Print</button>
      </div>
      {sheet.pages.map((page) => (
        <article className="presence-page" key={page.itemId}>
          <h1>{page.title}</h1>
          <p className="presence-page-where">
            {[page.room, page.when, page.presenters].filter(Boolean).join("  ·  ")}
          </p>
          <div
            className="presence-page-square"
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: squareFor(page.link) }}
          />
          <p className="presence-page-instruction">Scan the QR code to check in</p>
          <p className="presence-page-code">{formatPresenceCode(page.code)}</p>
          <p className="presence-page-fallback">
            No camera? Open {plainHost(page.link)} and enter that code.
          </p>
        </article>
      ))}
    </div>
  );
}

/**
 * Drawn as one path of black squares rather than an image file, so nothing is
 * fetched and it stays sharp at any size a printer chooses. Correction level H
 * rather than the screen's M: this page gets folded, taped to a door frame and
 * held under a hand, and the extra redundancy is what survives that.
 */
/** "wvcea.app/present" — an address a teacher can read off paper and type. */
function plainHost(link: string): string {
  try {
    const url = new URL(link);
    return `${url.host}${url.pathname}`;
  } catch {
    return "wvcea.app/present";
  }
}

function squareFor(text: string): string {
  const code = qrcode(0, "H");
  code.addData(text);
  code.make();
  const count = code.getModuleCount();
  let path = "";
  for (let row = 0; row < count; row += 1) {
    for (let column = 0; column < count; column += 1) {
      if (code.isDark(row, column)) path += `M${column} ${row}h1v1h-1z`;
    }
  }
  return [
    `<svg viewBox="-2 -2 ${count + 4} ${count + 4}" xmlns="http://www.w3.org/2000/svg" role="img">`,
    `<rect x="-2" y="-2" width="${count + 4}" height="${count + 4}" fill="#ffffff"/>`,
    `<path d="${path}" fill="#000000"/>`,
    `</svg>`,
  ].join("");
}

const PRINT_STYLES = `
.presence-sheet-message { font: 16px/1.5 system-ui, sans-serif; padding: 2rem; }
.presence-sheet-toolbar {
  font: 16px/1.5 system-ui, sans-serif; padding: 1rem 2rem; display: flex;
  align-items: center; justify-content: space-between; gap: 1rem;
  border-bottom: 1px solid #ccc;
}
.presence-sheet-toolbar button { font: inherit; padding: 0.5rem 1.5rem; cursor: pointer; }
.presence-page {
  box-sizing: border-box; width: 100%; max-width: 8.5in; min-height: 11in;
  margin: 0 auto; padding: 0.75in 0.6in; text-align: center;
  display: flex; flex-direction: column; align-items: center; justify-content: flex-start;
  font-family: system-ui, sans-serif; color: #000; background: #fff;
  border-bottom: 1px dashed #bbb;
}
.presence-page h1 { font-size: 40pt; line-height: 1.1; margin: 0 0 0.15in; font-weight: 700; color: #000; }
.presence-page-where { font-size: 16pt; margin: 0 0 0.3in; color: #333; }
.presence-page-square { width: 4.6in; max-width: 100%; }
.presence-page-square svg { width: 100%; height: auto; display: block; }
.presence-page-instruction { font-size: 17pt; margin: 0.25in 0 0.12in; }
.presence-page-code {
  font-size: 34pt; letter-spacing: 0.06em; margin: 0 0 0.1in; font-weight: 700;
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
}
.presence-page-fallback { font-size: 13pt; color: #444; margin: 0; }
@media print {
  /*
   * styles.css hides everything on the page when printing — one blunt rule
   * written for the Fine Arts packet, which then shows itself again. Every print
   * view in the application has to name itself the same way or it comes out as
   * correctly paginated blank paper, which is exactly what this page did.
   *
   * Higher specificity than the body-wide rule it is undoing, so document order
   * does not decide it.
   */
  .presence-sheet, .presence-sheet * { visibility: visible !important; }
  /*
   * The application's own page colour is a warm off-white. Left alone it prints
   * as a cream band across the foot of every sheet, below the page box — which
   * a real PDF showed and a screen never would.
   */
  html, body, .presence-sheet { background: #fff !important; }
  .presence-sheet-toolbar { display: none; }
  /*
   * Exactly the sheet, because @page takes the margin to zero. An earlier 10.4in
   * left six tenths of an inch of the application showing under each page.
   */
  .presence-page { border-bottom: none; page-break-after: always; break-after: page; min-height: auto; height: 11in; }
  .presence-page:last-child { page-break-after: auto; break-after: auto; }
  @page { size: letter portrait; margin: 0; }
}
`;
